import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson, performPostRequest } from '@atlassian/jira-fetch';
import {
	isSynthetic,
	X2C_SERVICE_URL_PREFIX,
} from '@atlassian/jira-project-create-importer-common';
import { JIRA_BUSINESS, type JiraThirdParty } from '../../../constants/import-type';
import type {
	AccessTokenStatus,
	ProjectData,
	StartThirdPartyImportPayload,
	ThirdPartyEntity,
	ThirdPartyEntityPage,
	ThirdPartyEntityStats,
	ThirdPartyEntityStatuses,
	ThirdPartyFolderAndSubfolders,
	ThirdPartyMembers,
} from '../../../types';
import type { ThirdPartyClient, ThirdPartyFieldConfigData } from '../types';

export class JiraClient implements ThirdPartyClient {
	private readonly BASE_URL: string;

	private readonly THIRD_PARTY_TYPE?: JiraThirdParty;

	private readonly BASE_HEADERS: Record<string, string> = {
		'x-issynthetic-traffic': String(isSynthetic()),
	};

	constructor({ cloudId, thirdPartyType }: { cloudId: string; thirdPartyType?: JiraThirdParty }) {
		this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/jsw`;
		if (ff('jira-business-to-software-project-imports') && thirdPartyType === JIRA_BUSINESS) {
			this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/jira`;
			this.THIRD_PARTY_TYPE = thirdPartyType;
		}
	}

	startImport({
		entityId,
		payload,
	}: {
		entityId: string;
		payload: StartThirdPartyImportPayload;
	}): Promise<{ migrationId: string }> {
		const url = `${this.BASE_URL}/project/${entityId}/migration/start`;
		return performPostRequest(url, {
			body: JSON.stringify(payload),
			headers: {
				...this.BASE_HEADERS,
				'Content-Type': 'application/json',
			},
		});
	}

	getEntitiesPage({ offset }: { offset: string | null }): Promise<ThirdPartyEntityPage> {
		let url = `${this.BASE_URL}/projects`;
		if (
			ff('jira-business-to-software-project-imports') &&
			this.THIRD_PARTY_TYPE === JIRA_BUSINESS
		) {
			url = `${url}/business`;
		}
		const urlWithOffset = offset ? `${url}?startAt=${offset}` : url;

		return fetchJson<ThirdPartyEntityPage>(urlWithOffset, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	searchEntities({ query }: { query: string }): Promise<ThirdPartyEntity[]> {
		let url = `${this.BASE_URL}/projects/search?query=${encodeURIComponent(query)}`;
		if (
			ff('jira-business-to-software-project-imports') &&
			this.THIRD_PARTY_TYPE === JIRA_BUSINESS
		) {
			url = `${this.BASE_URL}/projects/business/search?query=${encodeURIComponent(query)}`;
		}

		return fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	getEntityStats({ entityId }: { entityId: string }): Promise<ThirdPartyEntityStats> {
		const url = `${this.BASE_URL}/project/${entityId}/stats`;

		return fetchJson<ThirdPartyEntityStats>(url, {
			method: 'GET',
			headers: {
				...this.BASE_HEADERS,
			},
		});
	}

	getFieldConfig({
		entityId,
		entities,
		projectData,
	}: {
		entityId: string;
		entities: ThirdPartyEntity[];
		projectData: ProjectData;
	}): Promise<ThirdPartyFieldConfigData> {
		if (entities.length === 0 && ff('x2c-clickup-importer-multi-list')) {
			return Promise.reject(new Error('No entities provided'));
		}

		const url = ff('x2c-clickup-importer-multi-list')
			? `${this.BASE_URL}/project/${entities[0].id}/fields`
			: `${this.BASE_URL}/project/${entityId}/fields`;

		return fetchJson<ThirdPartyFieldConfigData>(url, {
			method: 'POST',
			body: JSON.stringify({
				projectDetails: {
					type: projectData.type,
					style: projectData.style,
				},
			}),
			headers: this.BASE_HEADERS,
		});
	}

	validateAccessToken(): Promise<{ status: AccessTokenStatus }> {
		return Promise.reject(new Error('validateAccessToken method is not implemented in Jira'));
	}

	postAccessToken(): Promise<void> {
		return Promise.reject(new Error('postAccessToken method is not implemented in Jira'));
	}

	getEntities(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('getEntities method is not implemented in Jira'));
	}

	getMultiEntityStats(): Promise<ThirdPartyEntityStats> {
		return Promise.reject(new Error('getMultiEntityStats method is not implemented in Jira'));
	}

	getEntityMembers(): Promise<ThirdPartyMembers[]> {
		return Promise.reject(new Error('getEntityMembers method is not implemented in Jira'));
	}

	getWorkspaces(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('getWorkspaces method is not implemented in Jira'));
	}

	getFolders(): Promise<ThirdPartyFolderAndSubfolders> {
		return Promise.reject(new Error('getFolders method is not implemented in jira'));
	}

	getSiteName(): Promise<string> {
		return Promise.reject(new Error('getSiteName method is not implemented in jira'));
	}

	getSpaces(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('getSpaces method is not implemented in jira'));
	}

	getMultiEntityStatuses(): Promise<ThirdPartyEntityStatuses> {
		return Promise.reject(new Error('getMultiEntityStatuses method is not implemented in jira'));
	}
}
